export const toTitleCase = str =>
  (str.charAt(0).toUpperCase() + str.substr(1).toLowerCase()).replace(
    /_/g,
    " "
  );

export const doesCareRecipient = careFor => {
  switch (careFor) {
    case "myself":
      return "do you";
    case "other":
      return "do they";
    default:
      return `does your ${careFor}`;
  }
};

export const isCareRecipient = careFor => {
  switch (careFor) {
    case "myself":
      return "are you";
    case "other":
      return "are they";
    default:
      return `is your ${careFor}`;
  }
};

export const careTypesOptions = [
  { label: "Live-in", id: "liveincare" },
  { label: "Day-time", id: "daytimecare" },
  { label: "Overnight", id: "overnightcare" }
];

export const formatCareTypes = careTypes =>
  `${careTypesOptions
    .filter(({ id }) => careTypes.includes(id))
    .map(({ label }) => label)
    .join(", ")
    .replace(/,([^,]*)$/, " and$1")} care`;

export const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

export const shortWeekdays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const timePeriods = ["Morning", "Afternoon", "Evening"];

export const roughTimes = {
  Morning: { start: "08:00", end: "12:00" },
  Afternoon: { start: "13:00", end: "17:00" },
  Evening: { start: "18:00", end: "22:00" }
};

export const dayTime = {
  start: 6,
  end: 22
};

export const liveinTime = {
  start: "00:30",
  end: "23:30"
};

export const accuracyOptions = [
  { label: "This is approximate", id: "approximate" },
  { label: "This is exact", id: "exact" },
  { label: "I don’t the know dates yet", id: "unknown" }
];

export const accuracyCategories = [
  { id: "unknown", description: "I don’t know" },
  { id: "exact", description: "I know the exact date" }
];

export const petsOptions = [
  { label: "Dog", id: "dog" },
  { label: "Cat", id: "cat" },
  { label: "Other", id: "other" },
  { label: "No, pet free", id: "none" }
];

export const genderOptions = [
  { label: "Female", id: "female" },
  { label: "Male", id: "male" },
  { label: "No preference", id: "doesnotmatter" }
];

export const driverOptions = [
  { label: "Yes, and they’ll need their own car", id: "yesandcarneeded" },
  { label: "Yes, and there’s a car they can use", id: "yesandnocarneeded" },
  { label: "No, they won’t need a car", id: "no" }
];

export const getNumberRank = number => {
  switch (number) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
};

export const getDates = date => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDate();
  const lastDay = new Date(year, month + 1, 0).getDate();

  return Array(lastDay - firstDay + 1)
    .fill()
    .map((_, index) => firstDay + index);
};

export const getRangeArray = (first, last, interval) => {
  if (first > last) {
    return [];
  }
  return Array((last - first) / interval + 1)
    .fill()
    .map((_, index) => first + index * interval);
};

export const doubleDigit = number =>
  number < 10 ? `0${number}` : `${number % 100}`;

export const getTimeValue = value => {
  const hour = doubleDigit(Math.floor(value));
  const minute = doubleDigit((value - hour) * 60);

  return `${hour}:${minute}`;
};

export const formatDateToString = date => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear() % 100;

  return `${doubleDigit(day)}.${doubleDigit(month)}.${doubleDigit(year)}`;
};

export const convertArrayToString = array =>
  array.join(", ").replace(/,([^,]*)$/, " &$1");

// Barebones email validation to ensure we get something we can create a need/lead with
export const validateEmail = email => {
  const re = /^\S+@\S+\.\S+$/;
  return re.test(email);
};

export const sortByKey = (array, key) =>
  array.sort((a, b) => {
    let x = a[key];
    let y = b[key];

    if (typeof x === "string") {
      x = `${x}`.toLowerCase();
    }
    if (typeof y === "string") {
      y = `${y}`.toLowerCase();
    }

    return x < y ? -1 : x > y ? 1 : 0;
  });

export const splitFullName = name => {
  let nameData = {};
  if (name) {
    const [firstname, ...otherNames] = name.trim().split(" ");
    // filter(Boolean) checks for emptiness for the strings in otherNames
    const lastname = otherNames.filter(Boolean).join(" ");
    nameData = { firstname, lastname, name };
  } else {
    nameData = { firstname: "", lastname: "", name: "" };
  }

  return nameData;
};

// turns a taxon_id, or list of ids into a string
export const renderTaxon = (taxonomyClassification, ids) => {
  if (ids && ids.length) {
    if (ids[0].taxon_id) {
      ids = ids.map(({ taxon_id }) => taxon_id);
    }

    const classificationsForIDs = taxonomyClassification.filter(taxon => {
      if (typeof ids === "string") {
        return ids === taxon.taxon_id;
      }
      return ids.includes(taxon.taxon_id);
    });

    if (classificationsForIDs.length) {
      return classificationsForIDs.map(taxon => taxon.value).join(", ");
    }

    return classificationsForIDs;
  }

  return "";
};

export const renderTaxonomiesFromArray = (values, taxonomy, key = null) => {
  if (!values.length) {
    return [];
  }

  return values.map(
    value => `${renderTaxon(taxonomy, key ? value[key] : value)}`
  );
};

export const createStringFromArray = array => {
  return array.reduce((string, district, index, arr) => {
    const districtString =
      arr.length > 1 && district === arr[arr.length - 1]
        ? ` and ${district}`
        : `, ${district}`;
    return string.concat(districtString);
  });
};
